import { Link } from "@tanstack/react-router";
import { ArrowUpRight } from "lucide-react";
import { useState } from "react";

import { Accent, GoLink, Section, displayTitle } from "@/features/portal/components/portal-ui";
import { cn } from "@/lib/utils";
import { m } from "@/paraglide/messages.js";

/** What a coach might set up for each goal. Examples of the shape of it, not promises of results. */
const GOALS = [
  {
    id: "cut",
    name: () => m.portal_goal_cut(),
    target: () => m.portal_goal_cut_target(),
    plan: () => m.portal_goal_cut_plan(),
    diet: () => m.portal_goal_cut_diet(),
  },
  {
    id: "bulk",
    name: () => m.portal_goal_bulk(),
    target: () => m.portal_goal_bulk_target(),
    plan: () => m.portal_goal_bulk_plan(),
    diet: () => m.portal_goal_bulk_diet(),
  },
  {
    id: "strength",
    name: () => m.portal_goal_strength(),
    target: () => m.portal_goal_strength_target(),
    plan: () => m.portal_goal_strength_plan(),
    diet: () => m.portal_goal_strength_diet(),
  },
  {
    id: "running",
    name: () => m.portal_goal_running(),
    target: () => m.portal_goal_running_target(),
    plan: () => m.portal_goal_running_plan(),
    diet: () => m.portal_goal_running_diet(),
  },
  {
    id: "mobility",
    name: () => m.portal_goal_mobility(),
    target: () => m.portal_goal_mobility_target(),
    plan: () => m.portal_goal_mobility_plan(),
    diet: () => m.portal_goal_mobility_diet(),
  },
  {
    id: "comeback",
    name: () => m.portal_goal_comeback(),
    target: () => m.portal_goal_comeback_target(),
    plan: () => m.portal_goal_comeback_plan(),
    diet: () => m.portal_goal_comeback_diet(),
  },
];

const half = "rounded-[20px] border border-border p-4";
const halfTitle =
  "flex items-center gap-2.5 text-[13px] font-semibold tracking-[0.06em] uppercase before:size-[22px] before:rounded-[7px] before:bg-primary";

/**
 * The idea of the app in one line, and a goal picker beside it that shows what it means: pick a
 * goal and see the plan and the diet a coach would set around it.
 */
export const GoalRhythm = () => {
  const [goalId, setGoalId] = useState(GOALS[0]?.id);
  const goal = GOALS.find((entry) => entry.id === goalId) ?? GOALS[0];

  return (
    <Section>
      <div className="grid items-center gap-10 lg:grid-cols-[1.1fr_1fr] lg:gap-14">
        <div className="px-1">
          <h2 className={cn(displayTitle, "text-[clamp(2.5rem,5.4vw,5rem)] leading-none")}>
            {m.portal_rhythm_title_lead()} <Accent>{m.portal_rhythm_title_tail()}</Accent>
          </h2>
          <p className="mt-5 max-w-[460px] text-lg leading-[1.55] text-muted-foreground">{m.portal_rhythm_body()}</p>
          <div className="mt-8 flex flex-wrap items-center gap-5">
            <GoLink to="/coaches" tone="lime">
              {m.portal_hero_find()}
            </GoLink>
            <Link to="/how-it-works" className="group inline-flex items-center gap-1.5 text-sm font-semibold">
              {m.portal_nav_how()}
              <ArrowUpRight
                aria-hidden
                className="size-4 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5"
              />
            </Link>
          </div>
        </div>

        <div>
          <div className="rounded-3xl border border-border p-4">
            <div role="group" aria-label={m.portal_goal_pick()} className="flex flex-wrap gap-2">
              {GOALS.map((entry) => (
                <button
                  key={entry.id}
                  type="button"
                  aria-pressed={entry.id === goal?.id}
                  className="h-[34px] rounded-full border border-border px-3.5 text-[13px] text-muted-foreground transition-colors hover:text-foreground aria-pressed:border-foreground aria-pressed:bg-foreground aria-pressed:font-semibold aria-pressed:text-background"
                  onClick={() => setGoalId(entry.id)}
                >
                  {entry.name()}
                </button>
              ))}
            </div>
            <div className="mt-4 flex min-h-[120px] flex-col justify-between gap-4 rounded-[18px] bg-[var(--site-well)] p-4">
              <span className="self-start rounded-full border border-border px-2.5 py-1 text-xs text-muted-foreground">
                {goal?.name()}
              </span>
              <p aria-live="polite" className="text-right text-[15px]">
                {goal?.target()}
              </p>
            </div>
          </div>
          <div className="mt-3 grid grid-cols-2 gap-3">
            <div className={half}>
              <b className={halfTitle}>{m.portal_week_tag_plan()}</b>
              <p className="mt-2.5 text-[13px] leading-[1.45] text-muted-foreground">{goal?.plan()}</p>
            </div>
            <div className={half}>
              <b className={halfTitle}>{m.portal_week_tag_diet()}</b>
              <p className="mt-2.5 text-[13px] leading-[1.45] text-muted-foreground">{goal?.diet()}</p>
            </div>
          </div>
        </div>
      </div>
    </Section>
  );
};
